<script >
import ChartingSidebarPanelAlerts from '@/features/charting-next/components/workspace/sidebar/ChartingSidebarPanelAlerts.vue';
import ChartingSidebarPanelAnalysisGroup from '@/features/charting-next/components/workspace/sidebar/ChartingSidebarPanelAnalysisGroup.vue';
import ChartingSidebarPanelDescription from '@/features/charting-next/components/workspace/sidebar/ChartingSidebarPanelDescription.vue';
import ChartingSidebarPanelError from '@/features/charting-next/components/workspace/sidebar/ChartingSidebarPanelError.vue';
import { useChartingSidebarState } from '@/features/charting-next/composables/useChartingSidebarState';
import { createChartingSidebarPanelItems } from '@/features/charting-next/models/chartingSidebarPanels';
import { useChartingToast as useToast } from '@/features/charting-next/services/chartingFeedback';
import { computed, ref, toRef, watch } from '@vue/composition-api';
const __sfc_main = {};
__sfc_main.props = {
  visible: {
    type: Boolean,
    default: false
  },
  stock: {
    type: Object,
    default: null
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const props = __props;
  const emit = __ctx.emit;
  const toast = useToast();
  const sidebar = useChartingSidebarState(toRef(props, 'stock'));
  const activePanel = ref('description');
  const panelItems = computed(() => createChartingSidebarPanelItems({
    capabilities: sidebar.capabilities.value
  }));
  const panelDescriptions = Object.freeze({
    description: 'Perfil, actividad y contexto de mercado de la compañía.',
    analysis: 'Niveles operativos y lectura financiera del valor.',
    alarms: 'Crea avisos de precio y consulta su estado.',
    error: 'Comunícanos cualquier dato que necesite revisión.'
  });
  const activePanelMeta = computed(() => {
    const item = panelItems.value.find(candidate => candidate.key === activePanel.value);
    return {
      icon: item?.icon || 'pi pi-info-circle',
      title: item?.title || 'Información',
      description: panelDescriptions[activePanel.value] || ''
    };
  });
  const operationalMetrics = computed(() => [{
    key: 'price',
    label: 'Precio',
    value: props.stock?.priceLabel || 'N/D'
  }, {
    key: 'entry',
    label: 'Entrada',
    value: props.stock?.entryPriceLabel || 'N/D'
  }, {
    key: 'stop',
    label: 'Stop',
    value: props.stock?.stopPriceLabel || 'N/D'
  }, {
    key: 'support',
    label: 'Soporte',
    value: props.stock?.supportPriceLabel || 'N/D'
  }]);
  const notify = ({
    severity = 'info',
    summary,
    detail
  }) => toast.add({
    severity,
    summary,
    detail,
    life: 3500
  });
  const selectPanel = item => {
    if (item.disabled) {
      notify({
        severity: 'warn',
        summary: item.title,
        detail: item.reason
      });
      return;
    }
    activePanel.value = item.key;
  };
  const createAlert = async () => {
    if (!sidebar.alertDraft.value.value) {
      notify({
        severity: 'warn',
        summary: 'Alarmas',
        detail: 'Configura un valor para la alarma.'
      });
      return;
    }
    try {
      const result = await sidebar.createAlert();
      notify({
        severity: 'success',
        summary: 'Alarma creada',
        detail: result?.message || 'La alarma se ha creado.'
      });
    } catch (error) {
      notify({
        severity: 'error',
        summary: 'Alarmas',
        detail: error.message || 'No se pudo crear la alarma.'
      });
    }
  };
  const removeAlert = async alertId => {
    try {
      const result = await sidebar.removeAlert(alertId);
      notify({
        severity: 'success',
        summary: 'Alarma eliminada',
        detail: result?.message || 'La alarma se ha eliminado.'
      });
    } catch (error) {
      notify({
        severity: 'error',
        summary: 'Alarmas',
        detail: error.message || 'No se pudo eliminar la alarma.'
      });
    }
  };
  const submitErrorReport = async () => {
    if (!sidebar.errorDraft.value.message.trim()) {
      notify({
        severity: 'warn',
        summary: 'Reportar error',
        detail: 'Describe la incidencia antes de enviarla.'
      });
      return;
    }
    try {
      const result = await sidebar.submitErrorReport();
      notify({
        severity: 'success',
        summary: 'Incidencia enviada',
        detail: result?.message || 'La incidencia se ha enviado.'
      });
    } catch (error) {
      notify({
        severity: 'error',
        summary: 'Reportar error',
        detail: error.message || 'No se pudo enviar la incidencia.'
      });
    }
  };
  watch(() => [props.visible, props.stock?.ticker], ([visible]) => {
    if (!visible) return;
    const preferred = panelItems.value.find(item => item.key === 'description' && !item.disabled) || panelItems.value.find(item => !item.disabled);
    activePanel.value = preferred?.key || 'description';
  });
  return {
    emit,
    sidebar,
    activePanel,
    panelItems,
    activePanelMeta,
    operationalMetrics,
    selectPanel,
    createAlert,
    removeAlert,
    submitErrorReport
  };
};
__sfc_main.components = Object.assign({
  ChartingSidebarPanelAnalysisGroup,
  ChartingSidebarPanelDescription,
  ChartingSidebarPanelAlerts,
  ChartingSidebarPanelError
}, __sfc_main.components);
export default __sfc_main;
</script>

<template>
    <Dialog
        :visible="visible"
        modal
        close-on-escape
        mask-class="charting-mobile-detail-mask"
        dialog-class="charting-mobile-detail-dialog"
        :dialog-style="{ width: '100vw' }"
        @update:visible="emit('update:visible', $event)"
    >
        <template #header>
            <div class="charting-mobile-detail__heading">
                <div class="charting-mobile-detail__eyebrow"><i class="pi pi-chart-line" aria-hidden="true"></i> Ficha del valor</div>
                <div class="charting-mobile-detail__ticker-row">
                    <strong>{{ (stock && stock.ticker) || 'Información' }}</strong>
                    <span v-if="stock && stock.entryTypeLabel">{{ stock.entryTypeLabel }}</span>
                    <b v-if="stock && stock.platinumScoreLabel">{{ stock.platinumScoreLabel }} pts</b>
                </div>
                <div class="charting-mobile-detail__company">{{ stock && (stock.companyName || stock.description) }}</div>
            </div>
        </template>

        <div class="charting-mobile-detail">
            <nav class="charting-mobile-detail__tabs" aria-label="Información del activo">
                <button
                    v-for="item in panelItems"
                    :key="item.key"
                    type="button"
                    :class="{ 'is-active': activePanel === item.key, 'is-disabled': item.disabled }"
                    :aria-current="activePanel === item.key ? 'page' : null"
                    @click="selectPanel(item)"
                >
                    <i :class="item.disabled ? 'pi pi-lock' : item.icon"></i>
                    <span>{{ item.label }}</span>
                </button>
            </nav>

            <section class="charting-mobile-detail__content">
                <div class="charting-mobile-detail__section-heading">
                    <span class="charting-mobile-detail__section-icon"><i :class="activePanelMeta.icon" aria-hidden="true"></i></span>
                    <div>
                        <h2>{{ activePanelMeta.title }}</h2>
                        <p>{{ activePanelMeta.description }}</p>
                    </div>
                </div>

                <dl v-if="activePanel === 'description'" class="charting-mobile-detail__snapshot">
                    <div v-for="metric in operationalMetrics" :key="metric.key">
                        <dt>{{ metric.label }}</dt>
                        <dd>{{ metric.value }}</dd>
                    </div>
                </dl>

                <ChartingSidebarPanelAnalysisGroup
                    v-if="activePanel === 'analysis'"
                    :stock="sidebar.selectedStock.value"
                    :fundamental-resource="sidebar.fundamentalResource"
                    :has-selected-ticker="Boolean(sidebar.selectedTicker.value)"
                    :can-view-basic-insights="sidebar.capabilities.value.canViewBasicInsights"
                    :can-use-fundamental="sidebar.capabilities.value.canUseFundamental"
                />
                <ChartingSidebarPanelDescription v-else-if="activePanel === 'description'" :stock="sidebar.selectedStock.value" :resource="sidebar.fundamentalResource" />
                <ChartingSidebarPanelAlerts
                    v-else-if="activePanel === 'alarms'"
                    :alert-draft="sidebar.alertDraft.value"
                    :alerts-resource="sidebar.alertsResource"
                    :pending-alerts="sidebar.pendingAlerts.value"
                    :executed-alerts="sidebar.executedAlerts.value"
                    :is-submitting-alert="sidebar.isSubmittingAlert.value"
                    @update:alert-draft="sidebar.alertDraft.value = $event"
                    @select-alert-field="sidebar.selectAlertField"
                    @create-alert="createAlert"
                    @remove-alert="removeAlert"
                />
                <ChartingSidebarPanelError
                    v-else-if="activePanel === 'error'"
                    :error-draft="sidebar.errorDraft.value"
                    :is-submitting-error="sidebar.isSubmittingError.value"
                    @update:error-draft="sidebar.errorDraft.value = $event"
                    @submit-error-report="submitErrorReport"
                />
            </section>
        </div>
    </Dialog>
</template>

<style scoped>
.charting-mobile-detail__heading { min-width: 0; }
.charting-mobile-detail__eyebrow { display: flex; align-items: center; gap: 0.35rem; margin-bottom: 0.28rem; color: var(--modern-accent, var(--brand-primary)); font-size: 0.62rem; font-weight: 850; letter-spacing: 0.09em; text-transform: uppercase; }
.charting-mobile-detail__ticker-row { display: flex; min-width: 0; align-items: center; gap: 0.45rem; }
.charting-mobile-detail__ticker-row strong { color: var(--modern-text, var(--p-text-color)); font-size: 1.12rem; letter-spacing: -0.02em; }
.charting-mobile-detail__ticker-row span, .charting-mobile-detail__ticker-row b { border: 1px solid var(--modern-border, var(--charting-b-border-color)); border-radius: 999px; background: var(--modern-bg, var(--charting-b-panel-bg)); padding: 0.18rem 0.45rem; color: var(--modern-muted, var(--p-text-muted-color)); font-size: 0.64rem; }
.charting-mobile-detail__ticker-row b { border-color: color-mix(in srgb, var(--modern-accent, var(--brand-primary)) 35%, transparent); background: var(--modern-accent-soft, var(--charting-b-hover-bg)); color: var(--modern-accent, var(--brand-primary)); }
.charting-mobile-detail__company { overflow: hidden; margin-top: 0.2rem; color: var(--modern-muted, var(--p-text-muted-color)); font-size: 0.76rem; text-overflow: ellipsis; white-space: nowrap; }
.charting-mobile-detail { display: flex; min-height: 100%; flex-direction: column; gap: 1rem; }
.charting-mobile-detail__tabs { position: sticky; z-index: 3; top: -0.85rem; display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 0.28rem; border: 1px solid var(--modern-border, var(--charting-b-border-color)); border-radius: 1rem; background: var(--modern-surface, var(--p-surface-0)); padding: 0.28rem; box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08); }
.charting-mobile-detail__tabs button { display: flex; min-width: 0; min-height: 3.25rem; flex-direction: column; align-items: center; justify-content: center; gap: 0.3rem; overflow: hidden; border: 0; border-radius: 0.75rem; background: transparent; color: var(--modern-muted, var(--p-text-muted-color)); font-size: 0.64rem; font-weight: 800; cursor: pointer; }
.charting-mobile-detail__tabs button i { font-size: 0.9rem; }
.charting-mobile-detail__tabs button span { max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.charting-mobile-detail__tabs button.is-active { background: var(--modern-accent-soft, var(--charting-b-hover-bg)); color: var(--modern-accent, var(--brand-primary)); box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--modern-accent, var(--brand-primary)) 25%, transparent); }
.charting-mobile-detail__tabs button.is-disabled { opacity: 0.48; }
.charting-mobile-detail__content { display: flex; min-height: 0; flex: 1 1 auto; flex-direction: column; gap: 0.9rem; }
.charting-mobile-detail__section-heading { display: flex; align-items: flex-start; gap: 0.75rem; padding: 0.15rem 0.1rem 0; }
.charting-mobile-detail__section-icon { display: grid; width: 2.5rem; height: 2.5rem; flex: 0 0 auto; place-items: center; border-radius: 0.8rem; background: var(--modern-accent-soft, var(--charting-b-hover-bg)); color: var(--modern-accent, var(--brand-primary)); }
.charting-mobile-detail__section-heading h2 { margin: 0; color: var(--modern-text, var(--p-text-color)); font-size: 1rem; font-weight: 850; letter-spacing: -0.01em; }
.charting-mobile-detail__section-heading p { margin: 0.2rem 0 0; color: var(--modern-muted, var(--p-text-muted-color)); font-size: 0.73rem; line-height: 1.35; }
.charting-mobile-detail__snapshot { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 0.4rem; margin: 0; }
.charting-mobile-detail__snapshot div { min-width: 0; border: 1px solid var(--modern-border, var(--charting-b-border-color)); border-radius: 0.82rem; background: var(--modern-surface, var(--p-surface-0)); padding: 0.65rem 0.5rem; box-shadow: 0 5px 18px rgba(15, 23, 42, 0.04); }
.charting-mobile-detail__snapshot dt { color: var(--modern-muted, var(--p-text-muted-color)); font-size: 0.58rem; font-weight: 800; letter-spacing: 0.05em; text-transform: uppercase; }
.charting-mobile-detail__snapshot dd { overflow: hidden; margin: 0.2rem 0 0; color: var(--modern-text, var(--p-text-color)); font-size: 0.75rem; font-weight: 850; text-overflow: ellipsis; white-space: nowrap; }

.charting-mobile-detail__content ::v-deep .charting-workspace-inspector__panel-body { display: flex; min-height: 0; flex-direction: column; gap: 0.75rem; overflow: visible; }
.charting-mobile-detail__content ::v-deep .charting-workspace-inspector__summary-cell,
.charting-mobile-detail__content ::v-deep .charting-workspace-inspector__event { border: 1px solid var(--modern-border, var(--charting-b-border-color)); border-radius: 1rem; background: var(--modern-surface, var(--p-surface-0)); padding: 0.9rem 1rem; box-shadow: 0 6px 22px rgba(15, 23, 42, 0.045); }
.charting-mobile-detail__content ::v-deep .charting-workspace-inspector__summary-label { color: var(--modern-muted, var(--p-text-muted-color)); font-size: 0.65rem; font-weight: 800; letter-spacing: 0.075em; text-transform: uppercase; }
.charting-mobile-detail__content ::v-deep .charting-workspace-inspector__summary-value { margin-top: 0.36rem; color: var(--modern-text, var(--p-text-color)); font-size: 0.9rem; font-weight: 750; line-height: 1.35; }
.charting-mobile-detail__content ::v-deep .charting-workspace-inspector__select,
.charting-mobile-detail__content ::v-deep .p-inputtext { width: 100%; border: 1px solid var(--modern-border, var(--charting-b-border-color)); border-radius: 0.8rem; background: var(--modern-bg, var(--charting-b-panel-bg)); color: var(--modern-text, var(--p-text-color)); }
.charting-mobile-detail__content ::v-deep .charting-workspace-inspector__link { display: inline-flex; align-items: center; margin-top: 0.4rem; color: var(--modern-accent, var(--brand-primary)); font-weight: 700; word-break: break-word; }
.charting-mobile-detail__content ::v-deep .charting-eurekers-analysis__grid,
.charting-mobile-detail__content ::v-deep .charting-fundamental-metric-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.55rem; }
.charting-mobile-detail__content ::v-deep .charting-eurekers-analysis__section h3,
.charting-mobile-detail__content ::v-deep .charting-fundamental-section__header h3 { color: var(--modern-muted, var(--p-text-muted-color)); font-size: 0.68rem; }
.charting-mobile-detail__content ::v-deep .charting-analysis-panel__switcher { border-color: var(--modern-border, var(--charting-b-border-color)); background: var(--modern-surface, var(--p-surface-0)); }
.charting-mobile-detail__content ::v-deep .charting-analysis-panel__switcher-button.is-active { background: var(--modern-accent, var(--brand-primary)); color: #fff; }
.charting-mobile-detail__content ::v-deep .charting-fundamental-metric,
.charting-mobile-detail__content ::v-deep .charting-fundamental-table__row,
.charting-mobile-detail__content ::v-deep .charting-fundamental-list { border-color: var(--modern-border, var(--charting-b-border-color)); background: var(--modern-surface, var(--p-surface-0)); box-shadow: 0 6px 22px rgba(15, 23, 42, 0.045); }
.charting-mobile-detail__content ::v-deep .brand-empty-state { border-color: var(--modern-border, var(--charting-b-border-color)); border-radius: 1rem; background: var(--modern-surface, var(--p-surface-0)); color: var(--modern-muted, var(--p-text-muted-color)); }

@media (max-width: 370px) {
    .charting-mobile-detail__snapshot { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .charting-mobile-detail__tabs button { font-size: 0.58rem; }
}
</style>

<style>
@media (max-width: 699px) {
    .p-dialog-mask.charting-mobile-detail-mask { top: var(--modern-shell-header-height, 62px); align-items: stretch; padding: 0; }
    .p-dialog.charting-mobile-detail-dialog { width: 100vw !important; height: calc(100dvh - var(--modern-shell-header-height, 62px)) !important; max-width: none; max-height: none; border: 0; border-radius: 0; background: var(--modern-bg, #f6f8fb); box-shadow: none; }
    .p-dialog.charting-mobile-detail-dialog .p-dialog-header { flex: 0 0 auto; border-color: var(--modern-border, var(--charting-b-border-color)); background: var(--modern-surface, #fff); padding: 0.75rem 1rem; }
    .p-dialog.charting-mobile-detail-dialog .p-dialog-header-close { background: var(--modern-bg, #f6f8fb); }
    .p-dialog.charting-mobile-detail-dialog .p-dialog-content { overflow-x: hidden; overflow-y: auto; scrollbar-width: none; -ms-overflow-style: none; overscroll-behavior: contain; -webkit-overflow-scrolling: touch; background: var(--modern-bg, #f6f8fb); padding: 0.85rem 0.75rem max(1rem, env(safe-area-inset-bottom)); }
    .p-dialog.charting-mobile-detail-dialog .p-dialog-content::-webkit-scrollbar { display: none; width: 0; height: 0; }
}
</style>
